import React, { useEffect, useRef, useState } from 'react';
import { Trash2, Upload } from 'lucide-react';
import server from '../../../api/server';
import avaAvatar from './defaultWidgetAvatar';
import joeyAvatar from './defaultJoeyAvatar.png';
import { secondaryButton } from '../../layouts/ConfigurationFormControls';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml', 'image/webp'];
const MAX_BYTES = 2 * 1024 * 1024;
const PRESETS = [{ name: 'Ava', src: avaAvatar }, { name: 'Joey', src: joeyAvatar }];

// Presets use the same upload and avatar_path contract as custom images.
export default function WidgetAvatarUpload({ context, widgetId, value, name, disabled, onChange, onPreview, onUploading }) {
  const input = useRef(null);
  const localUrl = useRef(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => () => { if (localUrl.current) URL.revokeObjectURL(localUrl.current); }, []);
  useEffect(() => {
    let active = true;
    setPreview(null);
    setError('');
    if (!value || !widgetId) { onPreview?.(null); return undefined; }
    server.generatePresignedUrl(context.client_id, widgetId, context.email, 'voice-agents', 'agents', value)
      .then(response => response.json())
      .then(body => { if (active && body.presigned_url) { setPreview(body.presigned_url); onPreview?.(body.presigned_url); } })
      .catch(() => { if (active) setError('Unable to show the current avatar.'); });
    return () => { active = false; };
    // onPreview is a setter from the editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, widgetId, context.client_id, context.email]);

  async function upload(getFile) {
    if (disabled || uploading || !widgetId) return;
    setError(''); setUploading(true); onUploading?.(true);
    const previous = preview;
    try {
      const file = await getFile();
      if (!ALLOWED_TYPES.includes(file.type)) throw new Error('Use a PNG, JPG, GIF, SVG or WebP image.');
      if (file.size > MAX_BYTES) throw new Error('Use an image under 2 MB.');
      const response = await server.uploadPublicAsset(context.client_id, widgetId, context.email, 'voice-agents', 'agents', file);
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body.file_path) throw new Error(body.message || 'Upload failed.');
      if (localUrl.current) URL.revokeObjectURL(localUrl.current);
      localUrl.current = URL.createObjectURL(file);
      setPreview(localUrl.current); onPreview?.(localUrl.current);
      onChange(body.file_path);
    } catch (err) {
      setPreview(previous); onPreview?.(previous);
      setError(err.message || 'Unable to upload the avatar.');
    } finally { setUploading(false); onUploading?.(false); }
  }

  function select(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) upload(() => file);
  }

  function selectPreset(preset) {
    upload(async () => {
      const response = await fetch(preset.src);
      if (!response.ok) throw new Error('Unable to load the default avatar.');
      return new File([await response.blob()], `default-${preset.name.toLowerCase()}.png`, { type: 'image/png' });
    });
  }

  const initials = (name || 'Ava').trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();
  return <div className="min-w-0" aria-busy={uploading}>
    <p className="block text-sm font-medium text-gray-700 mb-1">Avatar</p>
    <div className="flex flex-wrap items-center gap-3">
      <span className="w-14 h-14 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center text-gray-600 font-semibold shrink-0">
        {preview ? <img src={preview} alt="Assistant avatar" className="w-full h-full object-cover" /> : initials}
      </span>
      {PRESETS.map(preset => <button key={preset.name} type="button" className={secondaryButton} disabled={disabled || uploading || !widgetId} onClick={() => selectPreset(preset)}><img src={preset.src} alt="" className="w-8 h-8 rounded-full object-cover" />Default - {preset.name}</button>)}
      <input ref={input} type="file" accept={ALLOWED_TYPES.join(',')} className="hidden" onChange={select} />
      <button type="button" className={secondaryButton} disabled={disabled || uploading || !widgetId} onClick={() => input.current?.click()}><Upload size={14} />Custom upload</button>
      {value && <button type="button" className={secondaryButton} disabled={disabled || uploading} aria-label="Remove avatar" onClick={() => { onChange(null); setPreview(null); onPreview?.(null); }}><Trash2 size={14} /></button>}
    </div>
    <p role={error ? 'alert' : 'status'} className={`mt-1 text-xs ${error ? 'text-red-700' : 'text-gray-500'}`}>{error || (uploading ? 'Uploading avatar…' : widgetId ? 'Choose a default avatar or upload your own image, then save changes. Without an avatar, initials are shown.' : 'Save the widget once to choose or upload an avatar.')}</p>
  </div>;
}
